import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Save, Lock, Zap, MessageCircle, Building2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Configuracoes() {
  const [form, setForm] = useState({
    asaas_api_key: "", asaas_env: "sandbox", asaas_webhook_token: "",
    whatsapp_provider: "mock",
    zapi_instance: "", zapi_token: "", zapi_client_token: "",
    twilio_account_sid: "", twilio_auth_token: "", twilio_whatsapp_from: "",
    evolution_base_url: "", evolution_instance: "", evolution_token: "",
    company_name: "VM Corporate", company_phone: "", company_address: "Piabetá, Magé — RJ",
    auto_approve_reservations: true, block_no_package_reservations: false, block_overdue_clients: true,
    overdue_grace_days: 3, low_balance_threshold_pct: 15, expiring_alert_days: 7,
  });
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({});

  const refresh = async () => {
    try {
      const [{ data: s }, { data: st }] = await Promise.all([
        api.get("/settings/"),
        api.get("/payments/status").catch(() => ({ data: {} })),
      ]);
      const { _integrations, ...rest } = s || {};
      setForm(f => ({ ...f, ...rest }));
      setIntegrations(_integrations || {});
      setStatus(st || {});
    } catch { /* noop */ }
  };

  useEffect(() => { refresh(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Only send fields the user actually filled in (masked bullets are ignored on backend anyway,
      // and we NEVER want to overwrite env-configured secrets with a masked bullet).
      const clean = Object.fromEntries(
        Object.entries(form).filter(([, v]) =>
          v !== "" && v !== null && v !== undefined && !(typeof v === "string" && v.includes("•"))
        )
      );
      await api.put("/settings/", clean);
      toast.success("Configurações salvas");
      await refresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erro ao salvar");
    }
    finally { setLoading(false); }
  };

  const fld = (label, key, type = "text", placeholder = "") => (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</label>
      <input
        type={type}
        value={form[key] || ""}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm focus:border-[#14213D] outline-none"
        data-testid={`config-${key}`}
      />
    </div>
  );

  return (
    <form onSubmit={save} data-testid="admin-configuracoes">
      <div className="admin-page-header">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Sistema · Configurações</div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Configurações de produção</h1>
        </div>
        <button type="submit" disabled={loading} className="w-full sm:w-auto bg-[#D4AF37] text-[#14213D] px-5 py-2.5 rounded-md text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-settings">
          <Save size={14} /> {loading ? "Salvando…" : "Salvar configurações"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div
          className={`border rounded-lg p-4 ${
            (integrations.payment_configured_via_env || status.payment_live || status.asaas_live)
              ? "bg-emerald-50 border-emerald-200"
              : "bg-amber-50 border-amber-200"
          }`}
          data-testid="config-gateway-status"
        >
          <div className="flex items-center gap-2">
            {(integrations.payment_configured_via_env || status.payment_live)
              ? <CheckCircle2 size={16} className="text-emerald-700" />
              : <AlertTriangle size={16} className="text-amber-700" />}
            <span className={`text-[10px] uppercase tracking-wider font-bold ${
              (integrations.payment_configured_via_env || status.payment_live) ? "text-emerald-700" : "text-amber-700"
            }`}>
              {(integrations.payment_provider || status.payment_provider || "Gateway").toUpperCase()} · {
                (integrations.payment_configured_via_env || status.payment_live) ? "ATIVO" : "MOCK"
              }
            </span>
          </div>
          <div className="text-xs text-gray-700 mt-1">
            Ambiente: {integrations.payment_environment || status.payment_environment || "sandbox"}
            {" · "}
            Webhook HMAC: {integrations.payment_webhook_secret_set ? "configurado" : "ausente"}
          </div>
        </div>
        <div
          className={`border rounded-lg p-4 ${
            (integrations.whatsapp_live || status.whatsapp_live) ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
          }`}
          data-testid="config-whatsapp-status"
        >
          <div className="flex items-center gap-2">
            {(integrations.whatsapp_live || status.whatsapp_live)
              ? <CheckCircle2 size={16} className="text-emerald-700" />
              : <AlertTriangle size={16} className="text-amber-700" />}
            <span className={`text-[10px] uppercase tracking-wider font-bold ${
              (integrations.whatsapp_live || status.whatsapp_live) ? "text-emerald-700" : "text-amber-700"
            }`}>
              WhatsApp {(integrations.whatsapp_live || status.whatsapp_live) ? "ATIVO" : "MOCK"}
            </span>
          </div>
          <div className="text-xs text-gray-700 mt-1">
            Provedor: {integrations.whatsapp_provider || status.whatsapp_provider || "mock"}
            {integrations.zapi_configured_via_env && " · credenciais via .env"}
          </div>
        </div>
      </div>

      {/* Gateway de Pagamento */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-5">
        <div className="flex items-center gap-2 mb-1">
          <Zap size={16} className="text-[#D4AF37]" />
          <h2 className="font-display text-xl text-[#14213D]">Gateway de Pagamento — Mercado Pago</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          As credenciais do Mercado Pago (Access Token, Webhook Secret, Public Key) são fornecidas via
          variáveis de ambiente <code className="bg-gray-100 px-1 rounded text-[10px]">PAYMENT_ACCESS_TOKEN</code>,
          {" "}<code className="bg-gray-100 px-1 rounded text-[10px]">PAYMENT_WEBHOOK_SECRET</code>.
          Status atual: <strong>{integrations.payment_configured_via_env ? "Configurado (LIVE)" : "Não configurado"}</strong>.
        </p>
        <details className="mb-4">
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
            Rollback para Asaas (emergência)
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 p-3 bg-amber-50 border border-amber-200 rounded">
            {fld("Asaas API Key", "asaas_api_key", "password", "$aact_...")}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Ambiente</label>
              <select value={form.asaas_env || "sandbox"} onChange={(e) => setForm({ ...form, asaas_env: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="config-asaas_env">
                <option value="sandbox">Sandbox</option>
                <option value="production">Produção</option>
              </select>
            </div>
            {fld("Asaas Webhook Token", "asaas_webhook_token", "password", "obrigatório em produção")}
          </div>
        </details>
      </div>

      {/* WhatsApp */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-5">
        <div className="flex items-center gap-2 mb-1">
          <MessageCircle size={16} className="text-[#D4AF37]" />
          <h2 className="font-display text-xl text-[#14213D]">WhatsApp</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">Escolha o provedor e configure as credenciais.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Provedor</label>
            <select value={form.whatsapp_provider || "mock"} onChange={(e) => setForm({ ...form, whatsapp_provider: e.target.value })} className="w-full mt-1 border border-gray-200 rounded px-3 py-2 text-sm" data-testid="config-whatsapp_provider">
              <option value="mock">Mock (não enviar)</option>
              <option value="zapi">Z-API</option>
              <option value="twilio">Twilio</option>
              <option value="evolution">Evolution API</option>
            </select>
          </div>
        </div>

        {form.whatsapp_provider === "zapi" && (
          <div className="p-4 bg-gray-50 rounded">
            {integrations.zapi_configured_via_env && (
              <div className="mb-3 flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                <CheckCircle2 size={14} className="mt-0.5" />
                <span>
                  Z-API está configurada via <code className="bg-white px-1 rounded">ZAPI_INSTANCE</code>,{" "}
                  <code className="bg-white px-1 rounded">ZAPI_TOKEN</code> e{" "}
                  <code className="bg-white px-1 rounded">ZAPI_CLIENT_TOKEN</code> no <code className="bg-white px-1 rounded">.env</code>.
                  Deixe os campos abaixo vazios para manter as credenciais atuais — só preencha se quiser sobrescrever.
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {fld("Instance ID", "zapi_instance", "text", "3D1234567A")}
              {fld("Token", "zapi_token", "password")}
              {fld("Client Token", "zapi_client_token", "password", "opcional")}
            </div>
          </div>
        )}
        {form.whatsapp_provider === "twilio" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-gray-50 rounded">
            {fld("Account SID", "twilio_account_sid", "text", "ACxxx")}
            {fld("Auth Token", "twilio_auth_token", "password")}
            {fld("WhatsApp From", "twilio_whatsapp_from", "text", "+14155238886")}
          </div>
        )}
        {form.whatsapp_provider === "evolution" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4 bg-gray-50 rounded">
            {fld("Base URL", "evolution_base_url", "text", "https://...")}
            {fld("Instance", "evolution_instance", "text")}
            {fld("API Key", "evolution_token", "password")}
          </div>
        )}
      </div>

      {/* Company */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1">
          <Building2 size={16} className="text-[#D4AF37]" />
          <h2 className="font-display text-xl text-[#14213D]">Dados da empresa</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">Aparecem em relatórios e templates de e-mail.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {fld("Razão social/Marca", "company_name")}
          {fld("Telefone", "company_phone", "text", "+55 21 ...")}
          {fld("Endereço", "company_address")}
        </div>
      </div>

      {/* P1: Business rules */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1">
          <Zap size={16} className="text-[#D4AF37]" />
          <h2 className="font-display text-xl text-[#14213D]">Regras de reservas e alertas</h2>
        </div>
        <p className="text-xs text-gray-500 mb-4">Comportamento P1: pacotes de horas, inadimplência e alertas automáticos.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <label className="flex items-center gap-2 text-sm" data-testid="setting-auto-approve">
            <input type="checkbox" checked={!!form.auto_approve_reservations} onChange={e => setForm({ ...form, auto_approve_reservations: e.target.checked })} />
            Aprovar reservas automaticamente
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="setting-block-no-package">
            <input type="checkbox" checked={!!form.block_no_package_reservations} onChange={e => setForm({ ...form, block_no_package_reservations: e.target.checked })} />
            Bloquear reservas de clientes sem pacote ativo
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="setting-block-overdue">
            <input type="checkbox" checked={form.block_overdue_clients !== false} onChange={e => setForm({ ...form, block_overdue_clients: e.target.checked })} />
            Bloquear reservas de inadimplentes
          </label>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {fld("Dias de carência da inadimplência", "overdue_grace_days", "number", "3")}
          {fld("Saldo baixo (% restante)", "low_balance_threshold_pct", "number", "15")}
          {fld("Alerta de vencimento (dias)", "expiring_alert_days", "number", "7")}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-500">
          <span>Os alertas rodam automaticamente a cada 6h. Execute manualmente quando precisar:</span>
          <button
            type="button"
            onClick={async () => {
              try {
                const { data } = await api.post("/alerts/run");
                toast.success(`Alertas: ${data.low_balance} saldo baixo · ${data.expiring} expirando`);
              } catch { toast.error("Falha ao rodar alertas"); }
            }}
            className="border border-[#14213D] text-[#14213D] hover:bg-[#14213D] hover:text-white px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1 self-start"
            data-testid="run-alerts-btn"
          >
            <Zap size={12} /> Rodar alertas agora
          </button>
        </div>
      </div>

      <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded flex items-start gap-2 text-xs text-gray-600">
        <Lock size={14} className="text-gray-400 mt-0.5" />
        <div>
          Os dados sensíveis são armazenados de forma segura no banco. Após salvar, eles são exibidos mascarados. Pode reescrever para atualizar.
        </div>
      </div>
    </form>
  );
}
